import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './GuildWar.css';
import DefenderDetailModal from './DefenderDetailModal';
import TowerJumpNav from './TowerJumpNav';

const GuildWar = () => {
  const [season, setSeason] = useState(0);
  const [opponent, setOpponent] = useState('opponent');
  const [match, setMatch] = useState(1);
  const [tower, setTower] = useState('all');
  const [guildName, setGuildName] = useState('');
  const [defenders, setDefenders] = useState([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState('');
  const [authError, setAuthError] = useState('');
  const [selectedDefender, setSelectedDefender] = useState(null);

  // コンポーネントがマウントされたときにlocalStorageからトークンを取得
  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    if (storedToken) {
      setAuthToken(storedToken);
      verifyToken(storedToken);
    } else {
      setIsLoading(false);
    }
  }, []);

  // トークンの検証
  const verifyToken = async (token) => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        'https://rangers-api-3mfw.onrender.com/api/auth/verify-token',
        { token }
      );

      if (response.data.success) {
        setIsAuthenticated(true);
        setAuthToken(token);
      } else {
        // トークンが無効の場合、ログアウト処理
        handleLogout();
      }
    } catch (error) {
      console.error('Token verification error:', error);
      handleLogout();
    } finally {
      setIsLoading(false);
    }
  };

  // ログアウト処理
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    setAuthToken('');
    setIsAuthenticated(false);
  };

  const getStartOfWeek = (season) => {
    const today = new Date();
    const newDate = new Date(today.getTime());
    const offset = season * 7;
    newDate.setDate(newDate.getDate() - offset);
    return newDate.toISOString().split('T')[0];
  };

  const fetchGuildWarData = async () => {
    setIsLoading(true);
    try {
      setIsError(false);

      // 月曜日から火曜日12時頃まで、今週の敵データは取得できないことをチェック
      const now = new Date();
      const jstOffset = 9 * 60; // JSTはUTC+9
      const jstDate = new Date(now.getTime() + (jstOffset + now.getTimezoneOffset()) * 60000);
      const day = jstDate.getDay(); // 0:日, 1:月, 2:火, ...
      const hour = jstDate.getHours();

      if (season === 0 && opponent === 'opponent' && (day === 1 || (day === 2 && hour < 12))) {
        alert('月曜日から火曜日12:00までは対戦相手が確定していないため、敵の情報を表示できません。\n過去週のデータまたは味方の情報をご覧ください。');
        setIsLoading(false);
        setDefenders([]);
        return;
      }

      const requestDate = getStartOfWeek(season);

      // 月曜日または火曜日12時未満、今週、味方の場合はformAPIが使われるため、1回だけ呼び出す
      const isFormApiPeriod = season === 0 && opponent === 'ally' && (day === 1 || (day === 2 && hour < 12));

      if (tower === 'all') {
        if (isFormApiPeriod) {
          // formAPIの場合は1回だけ呼び出す（全タワーの情報が返ってくる）
          const response = await axios.get(
            'https://rangers-api-3mfw.onrender.com/api/guild-war-can',
            {
              params: {
                date: requestDate,
                opponent,
                match,
                tower: 1, // formAPIを使う場合、タワー番号は使われないが、1を指定
              },
              headers: {
                Authorization: `Bearer ${authToken}`
              }
            }
          );

          const data = response.data;
          setGuildName(data.guildName);
          // formAPIは全タワーの情報を返すが、towerNumberが含まれていない可能性があるため、そのまま設定
          setDefenders(data.result.guildWarDefenceDeckResponses || []);
        } else {
          // 通常API：全タワーのデータを並列取得
          const towerNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
          const promises = towerNumbers.map(towerNum =>
            axios.get(
              'https://rangers-api-3mfw.onrender.com/api/guild-war-can',
              {
                params: {
                  date: requestDate,
                  opponent,
                  match,
                  tower: towerNum,
                },
                headers: {
                  Authorization: `Bearer ${authToken}`
                }
              }
            )
          );

          const responses = await Promise.all(promises);
          const allDefenders = [];
          responses.forEach((response, index) => {
            const data = response.data;
            if (index === 0) {
              setGuildName(data.guildName);
            }
            const defendersWithTower = (data.result.guildWarDefenceDeckResponses || []).map(defender => ({
              ...defender,
              towerNumber: towerNumbers[index]
            }));
            allDefenders.push(...defendersWithTower);
          });
          setDefenders(allDefenders);
        }
      } else {
        // 単一タワーのデータ取得
        const response = await axios.get(
          'https://rangers-api-3mfw.onrender.com/api/guild-war-can',
          {
            params: {
              date: requestDate,
              opponent,
              match,
              tower,
            },
            headers: {
              Authorization: `Bearer ${authToken}`
            }
          }
        );

        const data = response.data;
        setGuildName(data.guildName);
        const defendersWithTower = (data.result.guildWarDefenceDeckResponses || []).map(defender => ({
          ...defender,
          towerNumber: tower
        }));
        setDefenders(defendersWithTower);
      }
    } catch (error) {
      setIsError(true);
      console.error('Error fetching data:', error);
      // 認証エラーの場合はログアウト
      if (error.response && error.response.status === 401) {
        handleLogout();
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchGuildWarData();
    }
  }, [season, opponent, match, tower, isAuthenticated]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');
    setIsLoading(true);

    try {
      const response = await axios.post(
        'https://rangers-api-3mfw.onrender.com/api/auth/verify-password',
        { password }
      );

      if (response.data.success) {
        const token = response.data.token;
        // トークンをlocalStorageに保存
        localStorage.setItem('authToken', token);
        setAuthToken(token);
        setIsAuthenticated(true);
      } else {
        setAuthError('パスワードが間違っています');
      }
    } catch (error) {
      console.error('Login error:', error);
      setAuthError('パスワードが間違っているか、サーバーに接続できません');
    } finally {
      setIsLoading(false);
      setPassword(''); // パスワードをクリア
    }
  };

  const getSwitchText = (equipMap) => {
    if (!equipMap.attr4No) {
      return '';
    }
    if (typeof equipMap.attr4No !== 'string') {
      equipMap.attr4No = String(equipMap.attr4No);
    }
    const prefix = equipMap.attr4No.substring(0, 2);
    const suffix = equipMap.attr4No.substring(4, 6);

    const switchTexts11 = {
      '01': '物攻',
      '02': '命中',
      '03': 'ス発',
      '04': 'ス命',
      '05': '攻ス',
      '06': '体力',
      '07': '回避',
      '08': 'ス回',
      '09': 'ス抵',
      '10': '物反',
      '11': '魔反',
      '12': 'ﾉｯｸ',
      '13': 'ｽﾀﾝ',
      '14': '沈黙',
      '15': '移動',
      '16': '攻範',
      '17': 'ミネ',
      '18': '再出',
    };

    const switchTexts12 = {
      '01': '物攻',
      '02': '命中',
      '03': '決確',
      '04': '決ダ',
      '05': 'ス発',
      '06': 'ス命',
      '07': '攻ス',
      '08': '体力',
      '09': '回避',
      '10': 'ス回',
      '11': 'ス抵',
      '12': 'ﾉｯｸ',
      '13': 'ｽﾀﾝ',
      '14': '沈黙',
      '15': '移動',
      '16': '攻範',
      '17': 'ミネ',
      '18': '再出',
    };

    const switchTexts13 = {
      '01': '魔攻',
      '02': '命中',
      '03': 'ス発',
      '04': 'ス命',
      '05': '攻ス',
      '06': '物防',
      '07': '魔防',
      '08': '体力',
      '09': '回避',
      '10': 'ス回',
      '11': 'ス抵',
      '12': 'ﾉｯｸ',
      '13': 'ｽﾀﾝ',
      '14': '沈黙',
      '15': '移動',
      '16': '攻範',
      '17': 'ミネ',
      '18': '再出',
    };

    const switchTexts14to18 = {
      '01': '物攻',
      '02': '魔攻',
      '03': '命中',
      '04': 'ス発',
      '05': 'ス命',
      '06': '攻ス',
      '07': '体力',
      '08': '回避',
      '09': 'ス回',
      '10': 'ス抵',
      '11': 'ﾉｯｸ',
      '12': 'ｽﾀﾝ',
      '13': '沈黙',
      '14': '移動',
      '15': '攻範',
      '16': 'ミネ',
      '17': '再出',
      '18': '属強',
      '19': '属命',
    };

    let switchText = '';
    if (prefix === '11') {
      switchText = switchTexts11[suffix];
    } else if (prefix === '12') {
      switchText = switchTexts12[suffix];
    } else if (prefix === '13') {
      switchText = switchTexts13[suffix];
    } else if (
      prefix === '14' ||
      prefix === '15' ||
      prefix === '16' ||
      prefix === '17' ||
      prefix === '18'
    ) {
      switchText = switchTexts14to18[suffix];
    }

    return switchText ? switchText + (equipMap.currentAdvancedEnchant ?? '?') : '不明';
  };

  // 防御者をタワー番号ごとにグループ化（ジャンプナビとセクション表示用）。
  const groupedByTower = useMemo(() => {
    const map = new Map();
    defenders.forEach((d) => {
      const key = d.towerNumber != null ? d.towerNumber : 'all';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(d);
    });
    return Array.from(map.entries()).sort((a, b) => {
      const numA = typeof a[0] === 'number' ? a[0] : Infinity;
      const numB = typeof b[0] === 'number' ? b[0] : Infinity;
      return numA - numB;
    });
  }, [defenders]);

  // ジャンプナビに出すタワー番号（数値のみ）。
  const towerNumbers = useMemo(
    () => groupedByTower.map(([t]) => t).filter((t) => typeof t === 'number'),
    [groupedByTower]
  );

  const selectGroup = (
    <div className="select-group">
      <h1>CaN</h1>
      <div>
        <label>Season:</label>
        <select
          onChange={(e) => setSeason(parseInt(e.target.value, 10))}
          value={season}
        >
          <option value="0">今週</option>
          <option value="1">1週前</option>
          <option value="2">2週前</option>
          <option value="3">3週前</option>
          <option value="4">4週前</option>
          <option value="5">5週前</option>
          <option value="6">6週前</option>
          <option value="7">7週前</option>
          <option value="8">8週前</option>
          <option value="9">9週前</option>
          <option value="10">10週前</option>
          <option value="11">11週前</option>
          <option value="12">12週前</option>
          <option value="13">13週前</option>
          <option value="14">14週前</option>
          <option value="15">15週前</option>
          <option value="16">16週前</option>
          <option value="17">17週前</option>
          <option value="18">18週前</option>
          <option value="19">19週前</option>
          <option value="20">20週前</option>
        </select>
      </div>

      <div>
        <label>Opponent:</label>
        <select onChange={(e) => setOpponent(e.target.value)} value={opponent}>
          <option value="ally">味方</option>
          <option value="opponent">敵</option>
        </select>
      </div>

      <div>
        <label>Match:</label>
        <select onChange={(e) => setMatch(parseInt(e.target.value, 10))} value={match}>
          <option value="1">1</option>
          <option value="2">2</option>
          <option value="3">3</option>
        </select>
      </div>

      <div>
        <label>Tower:</label>
        <select onChange={(e) => setTower(e.target.value === 'all' ? 'all' : parseInt(e.target.value, 10))} value={tower}>
          <option value="all">All</option>
          <option value="1">1</option>
          <option value="2">2</option>
          <option value="3">3</option>
          <option value="4">4</option>
          <option value="5">5</option>
          <option value="6">6</option>
          <option value="7">7</option>
          <option value="8">8</option>
          <option value="9">9</option>
          <option value="10">10</option>
        </select>
      </div>
      <br />
    </div>
  );

  return (
    <div className="guild-war-container">
      {!isAuthenticated ? (
        <div className="login-container">
          <h1>CaN</h1>
          <form onSubmit={handlePasswordSubmit}>
            <label>パスワードを入力してください：</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
            />
            <button type="submit" disabled={isLoading}>
              {isLoading ? 'ログイン中...' : '送信'}
            </button>
            {authError && <p className="auth-error">{authError}</p>}
          </form>
        </div>
      ) : (
        <>
          {selectGroup}
          {isError ? (
            <p>データの取得中にエラーが発生しました。後でもう一度お試しください。</p>
          ) : isLoading ? (
            <p>データ読み込み中...(数分ほどかかる場合があります)</p>
          ) : (
            <>
              <br />
              <h2>Guild Name: {guildName}</h2>
              {tower === 'all' && (
                <TowerJumpNav towers={towerNumbers} idPrefix="can-tower" />
              )}
              {groupedByTower.map(([towerKey, defs]) => (
                <section
                  key={towerKey}
                  id={`can-tower-${towerKey}`}
                  className="gw-tower-anchor"
                  data-tower={towerKey}
                >
                  {defs.map((defender, index) => {
                    const deckInfo = defender.deckInfoJson || [];
                const playerName = defender.mergedGuildPlayer?.name || '名前不明';
                const winCount = defender.winCount || '0';
                const remainDefenceCount = defender.remainDefenceCount || '0';
                let main = '';
                // remainDefenceCountが数値でも文字列でも正しく判定するためparseIntを使用
                if (parseInt(remainDefenceCount, 10) === 5) {
                  main = 'メイン';
                }

                return (
                  <div key={index} className="defender-card">
                    <br />
                    <h3>
                      {main} Tower{defender.towerNumber || tower} /{' '}
                      <span className="defender-name-tap" onClick={() => setSelectedDefender({ defender, towerLabel: defender.towerNumber || tower })}>
                        {playerName}
                      </span>
                    </h3>
                    <p>防衛 {winCount}-残 {remainDefenceCount}</p>
                    <table className="deck-table">
                      <thead>
                        <tr>
                          <th style={{ width: '30px' }}></th>
                          <th>ﾚﾝｼﾞｬｰ</th>
                          <th>武器</th>
                          <th>防具</th>
                          <th>ｱｸｾ </th>
                        </tr>
                      </thead>
                      <tbody>
                        {deckInfo.length === 0 ? (
                          <tr>
                            <td colSpan="5">デッキ情報がありません。</td>
                          </tr>
                        ) : (
                          deckInfo.playerUnitTeams?.['1']?.map((team, teamIndex) => (
                            <tr key={teamIndex}>
                              <td style={{ width: '30px' }}>{teamIndex + 1}</td>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                                  <div>
                                    <a href={`https://rangers.lerico.net/ja/ranger/${team.unitCode}`} target="_blank" rel="noopener noreferrer">
                                      <img
                                        src={`https://rangers.lerico.net/res/${team.unitCode}/${team.unitCode}-thum.png`}
                                        alt={`ユニット ${team.unitCode}`}
                                        className="unit-img"
                                        style={{ height: '30px', objectFit: 'contain', cursor: 'pointer' }}
                                      />
                                    </a>
                                    <div>{team.unitLevel}</div>
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                    {team.awakeAbilities?.[0]?.iconResourcePath && (
                                      <img
                                        src={`https://rangers.lerico.net/res/ability_icon/${team.awakeAbilities[0].iconResourcePath}`}
                                        alt="覚醒アビリティ"
                                        className="ability-icon"
                                        style={{ width: '20px', height: '20px' }}
                                      />
                                    )}
                                    {team.talentGrade > 0 && (
                                      <div style={{ fontSize: '15px', color: '#666' }}>
                                        才{team.talentGrade}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td>
                                {team.equipMap?.WEAPON ? (
                                  <>
                                    <a href={`https://rangers.lerico.net/ja/equipment/${team.equipMap.WEAPON.equipItemCode}`} target="_blank" rel="noopener noreferrer">
                                      <img
                                        src={`https://rangers.lerico.net/res/gear_icon/${team.equipMap.WEAPON.equipItemCode}_icon.png`}
                                        alt="武器"
                                        className="gear-img"
                                        style={{ width: '30px', height: '30px', cursor: 'pointer' }}
                                      />
                                    </a>
                                    <div>
                                      {getSwitchText(team.equipMap.WEAPON)}
                                      {team.equipMap.WEAPON.currentSkillPlusEnchant > 0 && (
                                        <span style={{ color: '#0066cc', fontSize: '13px' }}>+{team.equipMap.WEAPON.currentSkillPlusEnchant}</span>
                                      )}
                                    </div>
                                  </>
                                ) : 'なし'}
                              </td>
                              <td>
                                {team.equipMap?.ARMOR ? (
                                  <>
                                    <a href={`https://rangers.lerico.net/ja/equipment/${team.equipMap.ARMOR.equipItemCode}`} target="_blank" rel="noopener noreferrer">
                                      <img
                                        src={`https://rangers.lerico.net/res/gear_icon/${team.equipMap.ARMOR.equipItemCode}_icon.png`}
                                        alt="防具"
                                        className="gear-img"
                                        style={{ width: '30px', height: '30px', cursor: 'pointer' }}
                                      />
                                    </a>
                                    <div>
                                      {getSwitchText(team.equipMap.ARMOR)}
                                      {team.equipMap.ARMOR.currentSkillPlusEnchant > 0 && (
                                        <span style={{ color: '#0066cc', fontSize: '13px' }}>+{team.equipMap.ARMOR.currentSkillPlusEnchant}</span>
                                      )}
                                    </div>
                                  </>
                                ) : 'なし'}
                              </td>
                              <td>
                                {team.equipMap?.ACC ? (
                                  <>
                                    <a href={`https://rangers.lerico.net/ja/equipment/${team.equipMap.ACC.equipItemCode}`} target="_blank" rel="noopener noreferrer">
                                      <img
                                        src={`https://rangers.lerico.net/res/gear_icon/${team.equipMap.ACC.equipItemCode}_icon.png`}
                                        alt="アクセ"
                                        className="gear-img"
                                        style={{ width: '30px', height: '30px', cursor: 'pointer' }}
                                      />
                                    </a>
                                    <div>
                                      {getSwitchText(team.equipMap.ACC)}
                                      {team.equipMap.ACC.currentSkillPlusEnchant > 0 && (
                                        <span style={{ color: '#0066cc', fontSize: '13px' }}>+{team.equipMap.ACC.currentSkillPlusEnchant}</span>
                                      )}
                                    </div>
                                  </>
                                ) : 'なし'}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                );
                  })}
                </section>
              ))}
            </>
          )}
        </>
      )
      }
      {selectedDefender && (
        <DefenderDetailModal
          defender={selectedDefender.defender}
          towerLabel={selectedDefender.towerLabel}
          authToken={authToken}
          onClose={() => setSelectedDefender(null)}
        />
      )}
    </div >
  );
};

export default GuildWar;
